/**
 * "A hand on a fader wins" (OPBridge S324, `timeline_touch_override`; operator 2026-10-16, "1", then "the next bar,
 * but might be worth giving the user options"). A live CC that OPBridge receives for the port, channel and CC a running
 * timeline's automation also sends marks that control TOUCHED, and the run stops sending it. When it takes the control
 * back is the timeline's `touchMode`: the next bar line after the hand lets go (the default), when the curve reaches the
 * hand's value, or not until the song stops.
 *
 * OPBridge cannot see a hand let go (a fader just stops sending), so this module tells it: when a fader, a knob or an XY
 * pad is let go, `POST /timelines/opbuttons/touch {port, channel (0-based), cc, state: "release"}` to each bridge the
 * control sends to that does touch. A 404 `not_touched` (no timeline was moving it) is the common answer and is fine.
 * The port is the one the control sends on (the bridge's default output), which is the port the automation's cues use.
 */

import type { TimelineTouchMode } from '@opbuttons/schema';
import { getConnection, resolveBridges } from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';

const CONSUMER = 'opbuttons';

export const TOUCH_MODES: Array<{ mode: TimelineTouchMode; label: string; info: string }> = [
  { mode: 'nextBarAfterRelease', label: 'Next bar', info: 'The timeline takes the fader back on the next bar line after you let go.' },
  { mode: 'pickup', label: 'When it catches up', info: 'The timeline takes it back when its curve reaches where you left the fader.' },
  { mode: 'untilStop', label: 'When the song stops', info: 'Yours for the rest of the song; the timeline moves it again next time it plays.' },
];

export function bridgeTouches(cfg: BridgeConfig): boolean {
  return getConnection(cfg.id)?.health()?.capabilities?.['timeline_touch_override'] === true;
}

/** Tell every bridge `bridgeIds` names (undefined = the default bridge) that the hand let go of these CCs. */
export function releaseTouch(bridgeIds: string[] | undefined, ccs: Array<{ channel: number; cc: number }>): void {
  for (const target of bridgeIds === undefined ? [''] : bridgeIds) {
    for (const cfg of resolveBridges(target)) {
      if (!bridgeTouches(cfg)) continue;
      const port = cfg.defaultMidiOutput;
      const conn = getConnection(cfg.id);
      if (!port || !conn || conn.status() !== 'connected') continue;
      for (const { channel, cc } of ccs) {
        void conn.httpClient().touchTimeline(CONSUMER, { port, channel: channel & 0x0f, cc: cc & 0x7f, state: 'release' }).catch(() => { /* 404 not_touched, or gone: nothing to do */ });
      }
    }
  }
}
